import { Injectable } from "@angular/core";
import {
  CanActivate,
  CanActivateChild,
  CanDeactivate,
  CanLoad,
  Route,
  UrlSegment,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from "@angular/router";
import { EndSessionRequest } from "@azure/msal-browser";
import { firstValueFrom, Observable } from "rxjs";
import { MsalService } from "@azure/msal-angular";
import { AppConstants } from "../constants/constants";
import { UserProfileService } from "../_services/userprofile.service";
import { ADMIN_ROLES, Roles } from "../constants/Roles";
import { Userprofile } from "../_models/Userprofile";
import { AppRoutingPathType } from "../constants/AppRoutingPathType";
import { RoleService } from "../_services/role.service";

@Injectable({
  providedIn: "root",
})
export class AuthGuard
  implements CanActivate, CanActivateChild, CanDeactivate<unknown>, CanLoad
{
  constructor(
    private userProfileService: UserProfileService,
    private roleService: RoleService,
    private authService: MsalService,
    public router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.checkUserRoleAccess(route);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return true;
  }

  canDeactivate(
    component: unknown,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return true;
  }

  canLoad(
    route: Route,
    segments: UrlSegment[]
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return true;
  }

  async checkUserRoleAccess(route: ActivatedRouteSnapshot): Promise<boolean> {
    // Allowed role/s base on AppRoutingPathType
    let allowedRoles: number[] = [];

    if (
      route.data.appRoutingPathType === AppRoutingPathType.Learning ||
      route.data.appRoutingPathType === AppRoutingPathType.LearnerCourse
    ) {
      allowedRoles = [Roles.Learner];
    } else if (route.data.appRoutingPathType === AppRoutingPathType.Admin) {
      allowedRoles = (
        await firstValueFrom(this.roleService.getAdministratorRoles$())
      ).map((ar) => ar.id);

      if (allowedRoles.indexOf(Roles.SystemAdministrator) < 0) {
        allowedRoles.push(Roles.SystemAdministrator);
      }
    } else if (route.data.appRoutingPathType === AppRoutingPathType.Tutor) {
      allowedRoles = [Roles.Tutor];
    } else if (
      route.data.appRoutingPathType === AppRoutingPathType.OrganisationAndCentre
    ) {
      allowedRoles = [Roles.SystemAdministrator];
    } else if (
      route.data.appRoutingPathType === AppRoutingPathType.TutorResources
    ) {
      allowedRoles = [...ADMIN_ROLES, Roles.Tutor];
    } else if (route.data.appRoutingPathType == AppRoutingPathType.LearnerTransfer 
      || route.data.appRoutingPathType == AppRoutingPathType.LearnerCentreTransfer) {
      allowedRoles = [Roles.SystemAdministrator, Roles.OrganisationalAdministrator, Roles.GlobalAdministrator];
    } else if (route.data.appRoutingPathType == AppRoutingPathType.AggregateReports) {
      allowedRoles = [...ADMIN_ROLES];
    }

    // Get UserProfile
    let userProfile = await firstValueFrom(this.userProfileService
      .getOwnUserProfile$());

    // Save common and generic (unsecure) user details (NEVER store roleId here)
    sessionStorage.setItem(AppConstants.FIRST_NAME, userProfile.firstName);
    sessionStorage.setItem(AppConstants.LAST_NAME, userProfile.lastName);

    if (allowedRoles.indexOf(userProfile[AppConstants.ROLE_ID]) > -1) {
      return true;
    }

    return this.processNavigation(userProfile);
  }

  private processLogoutRedirectUser(): boolean {
    const request: EndSessionRequest = {
      postLogoutRedirectUri: AppConstants.WELCOME_PAGE,
    };

    this.authService.logoutRedirect(request);
    return false;
  }

  processNavigation(userProfile: Userprofile): boolean {
    if (userProfile) {
      switch (userProfile[AppConstants.ROLE_ID]) {
        case Roles.Learner:
          this.router.navigate([AppConstants.DASHBOARD_PAGE]);
          return false;

        case Roles.Tutor:
          this.router.navigate([AppConstants.TUTOR_PAGE]);
          return false;

        case Roles.SystemAdministrator:
        case Roles.GlobalAdministrator:
        case Roles.OrganisationalAdministrator:
          this.router.navigate([AppConstants.ADMIN_DASHBOARD_LEARNERS_PAGE]);
          return false;

        default:
          return this.processLogoutRedirectUser();
      }
    } else {
      return this.processLogoutRedirectUser();
    }
  }
}
